import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from 'swiper/modules';
import "swiper/css";
import "swiper/css/navigation";
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';

import three from "../assets/3.png";
import four from "../assets/4.png";
import five from "../assets/5.png";
import eight from "../assets/8.png";
import nine from "../assets/9.png";
import eleven from "../assets/11.png";
import twelve from "../assets/12.png";
import fifteen from "../assets/15.png";
import eighteen from "../assets/18.png";
import lablanka from "../assets/new (1).png";
import skyhotel from "../assets/new (3).png";
import budgetsafari from "../assets/new (4).png";
import evergreensinharaja from "../assets/new (5).png";
import fastx from "../assets/new (6).png";
import "./portfolio.css"

const ProjectCard = ({ image, title, description, link, alt, category }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [time, setTime] = useState(0);
  const cardRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(prev => prev + 0.016);
    }, 16);
    return () => clearInterval(interval);
  }, []);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePos({ x, y });
  };

  const getCategoryLabel = (cat) => {
    switch(cat) {
      case 'figma': return 'Design';
      case 'figmaWeb': return 'Web Design';
      case 'figmaMobile': return 'Mobile Design';
      case 'fullstack': return 'Full Stack';
      case 'frontend': return 'Frontend';
      default: return 'Project';
    }
  };

  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      <div 
        ref={cardRef}
        className="project-card-modern"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          transform: isHovered ? 
            `perspective(1000px) rotateY(${(mousePos.x - 0.5) * 10}deg) rotateX(${(mousePos.y - 0.5) * -10}deg) scale(1.02)` : 
            'perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1)',
          transformStyle: 'preserve-3d'
        }}
      >
        {/* Category Badge */}
        {category && (
          <div className={`category-badge ${category}`}>
            {getCategoryLabel(category)}
          </div>
        )}
        {/* Subtle Background Effects */}
        <div className="card-background">
          <svg className="morph-bg" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="subtleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#f8fafc', stopOpacity: 0.8 }} />
                <stop offset="50%" style={{ stopColor: '#f1f5f9', stopOpacity: 0.6 }} />
                <stop offset="100%" style={{ stopColor: '#e2e8f0', stopOpacity: 0.4 }} />
              </linearGradient>
            </defs>
            <polygon 
              points={`${50 + Math.sin(time) * 5},${20 + Math.cos(time * 0.8) * 3} ${80 + Math.sin(time * 1.2) * 4},${30 + Math.cos(time) * 4} ${90 + Math.sin(time * 0.6) * 3},${70 + Math.cos(time * 1.5) * 5} ${60 + Math.sin(time * 1.8) * 6},${85 + Math.cos(time * 0.4) * 4} ${20 + Math.sin(time * 0.9) * 5},${75 + Math.cos(time * 1.1) * 3} ${10 + Math.sin(time * 1.4) * 3},${40 + Math.cos(time * 0.7) * 4}`}
              fill="url(#subtleGradient)"
            />
          </svg>
        </div>

        {/* Grid Pattern */}
        <div className="grid-pattern"></div>

        {/* Image Container */}
        <div className="image-container">
          <img 
            src={image} 
            alt={alt}
            className="project-image"
          />
          <div className="image-overlay"></div>
        </div>

        {/* Content */}
        <div className="card-content">
          <div className="card-header">
            <h3 className="project-title">{title}</h3>
            <ArrowUpRight 
              size={20} 
              className="arrow-icon"
              style={{
                transform: isHovered ? 'translate(4px, -4px) rotate(45deg)' : 'translate(0, 0) rotate(0deg)'
              }}
            />
          </div>
          <p className="project-description">{description}</p>
          
          {/* Status Indicator */}
          <div className="status-indicator">
            <div className="status-dot"></div>
            <span>Live Project</span>
          </div>
        </div>

        {/* Hover Effects */}
        <div className="hover-glow"></div>
      </div>
    </a>
  );
};

function Works() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const swiperRef = useRef(null);
  const portfolioRef = useRef(null);

  const projectCategories = {
    figmaWeb: [
      {
        image: three,
        title: "Tourism Website",
        description: "A stylish and responsive tourism website design crafted in Figma for a seamless user journey.",
        link: "https://www.figma.com/proto/Af72rawUzfcztLVAmDT6fA/Untitled?node-id=3-3&p=f&t=KU3zc0QY65l8fJ4H-1&scaling=scale-down&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=3%3A3",
        alt: "tourism website",
        category: "figmaWeb"
      },
      {
        image: four,
        title: "Tourism Design",
        description: "A clean Figma design for a Sri Lanka tourism site with a soft, cloud-themed layout.",
        link: "https://www.figma.com/proto/TrK6HME2gCH0dMXvKGE8WZ/Untitled?node-id=3-267&t=pyVySSQiGAEYJTJp-1&scaling=min-zoom&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=3%3A267",
        alt: "tourism design",
        category: "figmaWeb"
      },
      {
        image: five,
        title: "Badminton Website",
        description: "A sleek Figma design for booking badminton courts in Sri Lanka with an easy, user-friendly layout.",
        link: "https://www.figma.com/proto/sr7jicvVKqEGFUp1qo6lL8/Badminton?node-id=4-152&p=f&t=PZNasupMVbW2B3j4-1&scaling=scale-down&content-scaling=fixed&page-id=0%3A1",
        alt: "Badminton App",
        category: "figmaWeb"
      },
      {
        image: eleven,
        title: "Ai Portfolio",
        description: "A modern AI-themed portfolio UI designed in Figma with a sleek, user-friendly layout.",
        link: "https://www.figma.com/proto/28AjEJT5fonjuNJZJ9PaI7/Untitled?node-id=1-81&p=f&t=sUHLCwWROmKZ7WVE-1&scaling=min-zoom&content-scaling=fixed&page-id=0%3A1",
        alt: "ai portfolio",
        category: "figmaWeb"
      },
      {
        image: twelve,
        title: "POS Dashboard",
        description: "A clean and intuitive POS dashboard UI for managing sales, inventory, and reports.",
        link: "https://www.figma.com/proto/lgL5Zgj3GyWC7dYfnfTiix/clothing-shop?node-id=75-4791&t=9qqHCFaluHzqfWZ7-1&scaling=min-zoom&content-scaling=fixed&page-id=27%3A4731&starting-point-node-id=75%3A4791",
        alt: "pos dashboard",
        category: "figmaWeb"
      },
    ],
    figmaMobile: [
      {
        image: eighteen,
        title: "Food Order Application",
        description: "Simple and user-friendly food ordering app design made in Figma.",
        link: "https://www.figma.com/proto/giN4n9PsScAIL3C3UoCAtr/IT22562760?node-id=1-221&p=f&t=qNeYi9xjXaDewISI-1&scaling=scale-down&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=1%3A221",
        alt: "Food Order Application",
        category: "figmaMobile"
      }
    ],
    fullstack: [
         {
        image: nine,
        title: "Yewon Lanka",
        description: "A clean, responsive tourism website for showcasing tours and services across Sri Lanka.",
        link: "https://yalayewontours.com/",
        alt: "tourism website",
        category: "fullstack"
      },
        {
        image: eight,
        title: "Happiness Villa",
        description: "A responsive hotel website near Yala, built with PHP, CSS, and JavaScript.",
        link: "https://happinessvillayala.lk/",
        alt: "tourism website",
        category: "fullstack"
      },
      {
        image: lablanka,
        title: "Lab Lanka",
        description: "A modern portfolio website for a laboratory equipment supplier in Sri Lanka, built with Next.js.",
        link: "https://www.lablanka.lk/",
        alt: "laboratory equipment website",
        category: "fullstack"
      },
      {
        image: skyhotel,
        title: "Sky Hotel Nuwara Eliya",
        description: "A responsive hotel website with an online booking system, built with PHP, CSS, and JavaScript.",
        link: "https://skyhotelnuwaraeliya.lk/",
        alt: "hotel website",
        category: "fullstack"
      },
      {
        image: budgetsafari,
        title: "Budget Safari",
        description: "A tourism landing page for Sri Lanka's most affordable wildlife safari experience, built with PHP, CSS, and JavaScript.",
        link: "https://bugetsafari.com/",
        alt: "tourism landing site",
        category: "fullstack"
      },
      {
        image: evergreensinharaja,
        title: "Evergreen Sinharaja",
        description: "A tourism website showcasing a rainforest retreat in Sri Lanka, built with the MERN stack.",
        link: "https://www.evergreensinharaja.com/",
        alt: "tourism website",
        category: "fullstack"
      },
      {
        image: fastx,
        title: "FastX",
        description: "An e-commerce website for tech and computer hardware in Sri Lanka, built with the MERN stack.",
        link: "https://fastx.lk/",
        alt: "e-commerce website",
        category: "fullstack"
      }
    ],
    frontend: [
        {
        image: fifteen,
        title: " Tourism Website",
        description: "A clean, responsive tourism website built with WordPress to showcase Sri Lanka tours.",
        link: "",
        alt: "Service Center",
        category: "frontend"
      },
      {
        image: lablanka,
        title: "Lab Lanka",
        description: "A modern portfolio website for a laboratory equipment supplier in Sri Lanka, built with Next.js.",
        link: "https://www.lablanka.lk/",
        alt: "laboratory equipment website",
        category: "frontend"
      },
      {
        image: budgetsafari,
        title: "Budget Safari",
        description: "A tourism landing page for Sri Lanka's most affordable wildlife safari experience, built with PHP, CSS, and JavaScript.",
        link: "https://bugetsafari.com/",
        alt: "tourism landing site",
        category: "frontend"
      }
    ]
  };

  const getAllProjects = () => {
    return [...projectCategories.figmaWeb, ...projectCategories.figmaMobile, ...projectCategories.fullstack, ...projectCategories.frontend];
  };

  const getFilteredProjects = () => {
    if (activeCategory === 'all') {
      return getAllProjects();
    }
    if (activeCategory === 'figma') {
      return [...projectCategories.figmaWeb, ...projectCategories.figmaMobile];
    }
    return projectCategories[activeCategory] || [];
  };

  const projects = getFilteredProjects();

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px 0px' }
    );

    if (portfolioRef.current) {
      observer.observe(portfolioRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle category changes and slider updates
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(0);
      setCurrentSlide(0);
    }
  }, [activeCategory]);

  // Handle category changes with smooth transitions
  const handleCategoryChange = (category) => {
    if (category === activeCategory) return;
    
    setTimeout(() => {
      setActiveCategory(category);
    }, 150);
  };

  const goToSlide = (index) => {
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(index);
      setCurrentSlide(index);
    }
  };

  const goNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const goPrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  return (
    <div className="portfolio" id="portfolio" ref={portfolioRef}>
      {/* Portfolio Header */}
      <div className={`portfolio-header ${isVisible ? 'slide-in-up' : ''}`}>
        {/* Category Filter */}
        <div className="category-filter">
          <button 
            className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('all')}
          >
            All Projects
          </button>
          <button 
            className={`filter-btn ${activeCategory === 'figma' || activeCategory === 'figmaWeb' || activeCategory === 'figmaMobile' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('figma')}
          >
            Figma Design
          </button>
          <button 
            className={`filter-btn ${activeCategory === 'fullstack' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('fullstack')}
          >
            Full Stack
          </button>
          <button 
            className={`filter-btn ${activeCategory === 'frontend' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('frontend')}
          >
            Frontend
          </button>
        </div>

        {/* Figma Subcategory Filter - Only shown when Figma Design is active */}
        {(activeCategory === 'figma' || activeCategory === 'figmaWeb' || activeCategory === 'figmaMobile') && (
          <div className="subcategory-filter">
            <button 
              className={`sub-filter-btn ${activeCategory === 'figmaWeb' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('figmaWeb')}
            >
              <span className="sub-icon">🌐</span>
              Web Design
            </button>
            <button 
              className={`sub-filter-btn ${activeCategory === 'figmaMobile' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('figmaMobile')}
            >
              <span className="sub-icon">📱</span>
              Mobile Design
            </button>
          </div>
        )}

        {/* Category Info */}
        <div className="category-info">
          {activeCategory === 'all' && (
            <span className="category-description">
              All projects ({getAllProjects().length} total)
            </span>
          )}
          {activeCategory === 'figma' && (
            <span className="category-description">
              All Figma designs - Web & Mobile ({projectCategories.figmaWeb.length + projectCategories.figmaMobile.length} projects)
            </span>
          )}
          {activeCategory === 'figmaWeb' && (
            <span className="category-description">
              Web UI/UX designs using Figma ({projectCategories.figmaWeb.length} projects)
            </span>
          )}
          {activeCategory === 'figmaMobile' && (
            <span className="category-description">
              Mobile app designs using Figma ({projectCategories.figmaMobile.length} projects)
            </span>
          )}
          {activeCategory === 'fullstack' && (
            <span className="category-description">
              Complete web applications with backend & frontend ({projectCategories.fullstack.length} projects)
            </span>
          )}
          {activeCategory === 'frontend' && (
            <span className="category-description">
              Frontend-focused applications ({projectCategories.frontend.length} projects)
            </span>
          )}
        </div>
      </div>

      <div className="portfolio-slider-wrapper">
        <button
          className="nav-arrow-side nav-arrow-side--left"
          onClick={goPrev}
          aria-label="Previous project"
        >
          <ChevronLeft />
        </button>

        <Swiper
        modules={[Navigation]}
        spaceBetween={60}
        slidesPerView={3}
        centeredSlides={true}
        grabCursor={true}
        loop={projects.length > 2}
        loopedSlides={projects.length}
        speed={600}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setCurrentSlide(swiper.realIndex);
        }}
        className="portfolio-slider"
        breakpoints={{
          320: { 
            slidesPerView: 1, 
            spaceBetween: 20,
            centeredSlides: true 
          },
          768: { 
            slidesPerView: 2, 
            spaceBetween: 30,
            centeredSlides: true 
          },
          1024: { 
            slidesPerView: 3, 
            spaceBetween: 60,
            centeredSlides: true 
          }
        }}
      >
        {projects.length > 0 ? (
          projects.map((project, index) => (
            <SwiperSlide key={`${activeCategory}-${index}-${project.title}`} className="portfolio-slide">
              <ProjectCard {...project} />
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide className="portfolio-slide empty-state">
            <div className="empty-state-card">
              <div className="empty-state-icon">🚀</div>
              <h3>Coming Soon</h3>
              <p>Frontend projects are currently in development. Check back soon!</p>
            </div>
          </SwiperSlide>
        )}
        </Swiper>

        <button
          className="nav-arrow-side nav-arrow-side--right"
          onClick={goNext}
          aria-label="Next project"
        >
          <ChevronRight />
        </button>
      </div>

      {/* Navigation */}
      <div className={`portfolio-nav-bar ${isVisible ? 'controls-animate-in' : ''}`}>
        <button
          className="nav-arrow"
          onClick={goPrev}
          aria-label="Previous project"
        >
          <ChevronLeft />
        </button>

        <div className="progress-dots">
          {projects.map((_, index) => (
            <button
              key={index}
              className={`progress-dot ${currentSlide === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to project ${index + 1}`}
              title={`Project ${index + 1} of ${projects.length}`}
            />
          ))}
        </div>

        <button
          className="nav-arrow"
          onClick={goNext}
          aria-label="Next project"
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  );
}

export default Works;
